/**
 * CommanderSection - Prominent commander and companion display for deck editor views
 *
 * Renders commander and companion card(s) with visual prominence:
 * - Commander: Gold/amber border treatment, crown icon indicator
 * - Companion: Purple border treatment, companion badge
 * - Support for partner commanders side-by-side
 *
 * For list variant, uses DeckListViewRow to maintain column alignment with main grid.
 * For grid/stack variants, uses specialized card layouts.
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';
import { FaMinus, FaPlus } from 'react-icons/fa';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import FlippableCardImage from '@/components/FlippableCardImage/FlippableCardImage';
import { isCommanderFormat } from '@/lib/deckUtils';
import type { DeckFormat, DeckStatus } from '@/types/deck';
import useDeckCardOperations from '../hooks/useDeckCardOperations';
import type { DeckColumnVisibility } from '../state/deckEditorSettings';
import { EnrichedDeckCard } from '../types';
import DeckCardRow from './DeckListViewRow';
import SectionTitle from './SectionTitle';

// ============================================================================
// Types
// ============================================================================

export interface CommanderSectionProps {
  /** Deck ID */
  deckId: string;
  /** Commander card(s) - supports partner commanders */
  commanders: EnrichedDeckCard[];
  /** Companion card (max 1) */
  companions?: EnrichedDeckCard[];
  /** Whether the deck is locked */
  isLocked?: boolean;
  /** Display variant: 'list' | 'grid' | 'stack' */
  variant: 'list' | 'grid' | 'stack';
  /** Deck format for zone rules */
  deckFormat?: DeckFormat | null;
  /** Deck status for zone rules */
  deckStatus?: DeckStatus;
  /** Cover card ID for highlighting */
  coverCardId?: string | null;
  /** Callback to set cover card */
  onSetCover?: (scryfallId: string) => void;
  /** Called when a card is selected to open details panel */
  onCardSelect?: (scryfallId: string, deckCardId: string) => void;
  /** Column visibility settings for list view column headers */
  columnVisibility?: DeckColumnVisibility;
}

// ============================================================================
// Grid Variant - Hero-sized card with gold/purple border/glow
// ============================================================================

interface CommanderGridCardProps {
  deckCard: EnrichedDeckCard;
  isLocked: boolean;
  isCompanion?: boolean;
  onDecrement?: () => void;
  onCardSelect?: () => void;
  isLoading?: boolean;
}

function CommanderGridCard({
  deckCard,
  isLocked,
  isCompanion = false,
  onDecrement,
  onCardSelect,
  isLoading,
}: CommanderGridCardProps): ReactElement {
  const { card, quantity } = deckCard;

  const imageUris = deckCard.display_image_uris;
  const borderColor = isCompanion ? 'border-purple-500' : 'border-amber-500';
  const shadowColor = isCompanion ? 'shadow-purple-500/20' : 'shadow-amber-500/20';

  return (
    <div className="group relative">
      <CardHoverPreview imageUris={imageUris} name={card.name} layout={card.layout} cardFaces={card.card_faces}>
        <button
          type="button"
          onClick={onCardSelect}
          className={clsx(
            'relative overflow-hidden rounded-xl border-2',
            borderColor,
            'shadow-lg',
            shadowColor,
            'transition-transform hover:scale-[1.02]',
            'cursor-pointer',
          )}
        >
          <FlippableCardImage
            imageUris={imageUris}
            layout={card.layout}
            cardFaces={card.card_faces}
            name={card.name}
            size="normal"
            className="h-auto! w-full!"
          />

          {/* Quantity Badge */}
          {quantity > 1 && (
            <div className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-zinc-900/90 text-sm font-bold text-zinc-100 shadow-md">
              {quantity}
            </div>
          )}

          {/* Hover Controls Overlay */}
          {!isLocked && (
            <div
              className={clsx(
                'invisible absolute inset-0 flex flex-col justify-end bg-linear-to-t from-black/80 via-black/20 to-transparent p-3 opacity-0 transition-opacity',
                'group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100',
              )}
            >
              <p className="mb-2 truncate text-sm font-medium text-zinc-100">{card.name}</p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onDecrement}
                  disabled={isLoading}
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-800/90 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                  aria-label="Decrease quantity"
                >
                  <FaMinus className="h-3 w-3" />
                </button>
                <span className="w-8 text-center text-sm font-bold text-zinc-100">{quantity}</span>
                <button
                  type="button"
                  disabled
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-800/90 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                  aria-label="Increase quantity"
                >
                  <FaPlus className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}
        </button>
      </CardHoverPreview>
    </div>
  );
}

// ============================================================================
// Stack Variant - Standalone full card (not stacked)
// ============================================================================

interface CommanderStackCardProps {
  deckCard: EnrichedDeckCard;
  isLocked: boolean;
  isCompanion?: boolean;
  isOrphaned?: boolean;
  onDecrement?: () => void;
  onCardSelect?: () => void;
  isLoading?: boolean;
}

function CommanderStackCard({
  deckCard,
  isLocked,
  isCompanion = false,
  isOrphaned = false,
  onDecrement,
  onCardSelect,
  isLoading,
}: CommanderStackCardProps): ReactElement {
  const { card, quantity } = deckCard;
  const cardWidth = 176; // Slightly larger than stack cards (w-44)

  const imageUris = deckCard.display_image_uris;
  const borderColor = isCompanion ? 'border-purple-500' : 'border-amber-500';
  const shadowColor = isCompanion ? 'shadow-purple-500/25' : 'shadow-amber-500/25';

  return (
    <div className="group/commander">
      {/* Header */}
      <SectionTitle isCommander={!isCompanion} isCompanion={isCompanion} isOrphaned={!isCompanion && isOrphaned} />

      {/* Card */}
      <CardHoverPreview imageUris={imageUris} name={card.name} layout={card.layout} cardFaces={card.card_faces}>
        <button
          type="button"
          onClick={onCardSelect}
          className={clsx(
            'relative overflow-hidden rounded-xl border-2',
            borderColor,
            'shadow-lg',
            shadowColor,
            'transition-transform hover:scale-[1.02]',
            'cursor-pointer',
          )}
          style={{ width: `${cardWidth}px` }}
        >
          <CardImage imageUris={imageUris} name={card.name} size="normal" className="h-auto! w-full!" />

          {/* Quantity Badge */}
          {quantity > 1 && (
            <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900/90 text-xs font-bold text-zinc-100 shadow">
              {quantity}
            </div>
          )}

          {/* Hover Controls */}
          {!isLocked && (
            <div className="invisible absolute top-2 left-2 flex gap-0.5 opacity-0 transition-opacity group-focus-within/commander:visible group-focus-within/commander:opacity-100 group-hover/commander:visible group-hover/commander:opacity-100">
              <button
                type="button"
                disabled
                className="flex h-6 w-6 items-center justify-center rounded bg-zinc-800/90 text-zinc-300 shadow hover:bg-zinc-700"
                aria-label="Increase quantity"
              >
                <FaPlus className="h-2.5 w-2.5" />
              </button>
              <button
                type="button"
                onClick={onDecrement}
                disabled={isLoading}
                className="flex h-6 w-6 items-center justify-center rounded bg-zinc-800/90 text-zinc-300 shadow hover:bg-zinc-700"
                aria-label="Decrease quantity"
              >
                <FaMinus className="h-2.5 w-2.5" />
              </button>
            </div>
          )}
        </button>
      </CardHoverPreview>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function CommanderSection({
  deckId,
  commanders,
  companions = [],
  isLocked = false,
  variant,
  deckFormat = null,
  deckStatus = 'brewing',
  coverCardId = null,
  onSetCover,
  onCardSelect,
  columnVisibility,
}: CommanderSectionProps): ReactElement | null {
  const { decrease, isLoading } = useDeckCardOperations({ deckId, isLocked });
  const commanderZoneOrphaned = !isCommanderFormat(deckFormat ?? null);

  // Don't render if no commanders or companions
  if (commanders.length === 0 && companions.length === 0) {
    return null;
  }

  // List variant - use DeckListViewRow for unified column alignment
  // Return fragment so SectionTitle and DeckCardRow participate in parent CSS Grid
  if (variant === 'list') {
    return (
      <>
        {/* Commander section header + rows */}
        {commanders.length > 0 && (
          <>
            <SectionTitle isCommander isOrphaned={commanderZoneOrphaned} columnVisibility={columnVisibility} />
            {commanders.map((cmd) => (
              <DeckCardRow
                key={cmd.id}
                deckCard={cmd}
                deckId={deckId}
                deckFormat={deckFormat}
                deckStatus={deckStatus}
                isCover={cmd.scryfall_id === coverCardId}
                isLocked={isLocked}
                isCommander
                onSetCover={onSetCover ?? (() => {})}
                onCardSelect={onCardSelect}
              />
            ))}
          </>
        )}

        {/* Companion section header + rows */}
        {companions.length > 0 && (
          <>
            <SectionTitle isCompanion columnVisibility={columnVisibility} />
            {companions.map((comp) => (
              <DeckCardRow
                key={comp.id}
                deckCard={comp}
                deckId={deckId}
                deckFormat={deckFormat}
                deckStatus={deckStatus}
                isCover={comp.scryfall_id === coverCardId}
                isLocked={isLocked}
                isCompanion
                onSetCover={onSetCover ?? (() => {})}
                onCardSelect={onCardSelect}
              />
            ))}
          </>
        )}
      </>
    );
  }

  // Grid variant - hero-sized cards
  if (variant === 'grid') {
    return (
      <section className="mb-8">
        {/* Commander cards */}
        {commanders.length > 0 && (
          <>
            <SectionTitle isCommander isOrphaned={commanderZoneOrphaned} />
            <div className="flex flex-wrap gap-4">
              {commanders.map((cmd) => (
                <div key={cmd.id} className="w-48 xl:w-56">
                  <CommanderGridCard
                    deckCard={cmd}
                    isLocked={isLocked}
                    onDecrement={() => decrease(cmd.id, cmd.quantity)}
                    onCardSelect={cmd.card ? () => onCardSelect?.(cmd.card.id, cmd.id) : undefined}
                    isLoading={isLoading}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Companion cards */}
        {companions.length > 0 && (
          <div className={commanders.length > 0 ? 'mt-6' : ''}>
            <SectionTitle isCompanion />
            <div className="flex flex-wrap gap-4">
              {companions.map((comp) => (
                <div key={comp.id} className="w-48 xl:w-56">
                  <CommanderGridCard
                    deckCard={comp}
                    isLocked={isLocked}
                    isCompanion
                    onDecrement={() => decrease(comp.id, comp.quantity)}
                    onCardSelect={comp.card ? () => onCardSelect?.(comp.card.id, comp.id) : undefined}
                    isLoading={isLoading}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    );
  }

  // Stack variant - standalone full cards side-by-side
  return (
    <section className="mb-6">
      <div className="flex flex-wrap gap-6">
        {/* Commander cards */}
        {commanders.map((cmd) => (
          <CommanderStackCard
            key={cmd.id}
            deckCard={cmd}
            isLocked={isLocked}
            isOrphaned={commanderZoneOrphaned}
            onDecrement={() => decrease(cmd.id, cmd.quantity)}
            onCardSelect={cmd.card ? () => onCardSelect?.(cmd.card.id, cmd.id) : undefined}
            isLoading={isLoading}
          />
        ))}

        {/* Companion cards */}
        {companions.map((comp) => (
          <CommanderStackCard
            key={comp.id}
            deckCard={comp}
            isLocked={isLocked}
            isCompanion
            onDecrement={() => decrease(comp.id, comp.quantity)}
            onCardSelect={comp.card ? () => onCardSelect?.(comp.card.id, comp.id) : undefined}
            isLoading={isLoading}
          />
        ))}
      </div>
    </section>
  );
}
